import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useMenuDialog } from '@/components/ui/use-menu-dialog';
import { EyeOpenIcon } from '@radix-ui/react-icons';
import { ArrowUpDown, EyeOff, Menu, Pencil, Trash2Icon } from 'lucide-react';
import { Account, AccountState } from '@liquidbudget/common';
import { AccountRestoreDialog } from '@/components/account-form/account-restore-dialog';
import { AccountArchiveDialog } from '@/components/account-form/account-archive-dialog';
import { AccountDeleteDialog } from '@/components/account-form/account-delete-dialog';
import { AccountSortDialogContent } from '@/components/account-form/account-sort-dialog-content';
import { AccountAddDialogContent } from '@/components/account-form/account-add-dialog-content';

type Props = {
  account: Account;
  disableHover?: boolean;
};

export function AccountMenu({ account, disableHover }: Props) {
  const { dialogType, triggerRef, openDialog, closeDialog, dialogProps, dropdownProps } = useMenuDialog();
  const isArchived = account.state === AccountState.ARCHIVED;

  return (
    <Dialog {...dialogProps}>
      <DropdownMenu {...dropdownProps}>
        <DropdownMenuTrigger asChild>
          <Button ref={triggerRef} variant="ghost" size="iconXs" className={`ml-3`}>
            <Menu
              size={16}
              className={disableHover ? 'text-slate-400' : `text-slate-200 group-hover/item:text-slate-600`}
            />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuLabel>{account.name}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            {!isArchived && (
              <DropdownMenuItem onClick={() => openDialog('edit')}>
                <Pencil className="mr-2 h-4 w-4" />
                <span>Edit</span>
              </DropdownMenuItem>
            )}
            {!isArchived && (
              <DropdownMenuItem onClick={() => openDialog('sort')}>
                <ArrowUpDown className="mr-2 h-4 w-4" />
                <span>Sort</span>
              </DropdownMenuItem>
            )}
            {!isArchived && (
              <DropdownMenuItem onClick={() => openDialog('archive')}>
                <EyeOff className="mr-2 h-4 w-4" />
                <span>Archive</span>
              </DropdownMenuItem>
            )}
            {isArchived && (
              <DropdownMenuItem onClick={() => openDialog('restore')}>
                <EyeOpenIcon className="mr-2 h-4 w-4" />
                <span>Restore</span>
              </DropdownMenuItem>
            )}
            {isArchived && (
              <DropdownMenuItem onClick={() => openDialog('delete')}>
                <Trash2Icon className="mr-2 h-4 w-4" />
                <span>Delete</span>
              </DropdownMenuItem>
            )}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
      {dialogType === 'edit' && <AccountAddDialogContent account={account} onClose={closeDialog} />}
      {dialogType === 'sort' && <AccountSortDialogContent onClose={closeDialog} />}
      {dialogType === 'archive' && <AccountArchiveDialog account={account} onClose={closeDialog} />}
      {dialogType === 'restore' && <AccountRestoreDialog account={account} onClose={closeDialog} />}
      {dialogType === 'delete' && <AccountDeleteDialog account={account} onClose={closeDialog} />}
    </Dialog>
  );
}
